<script setup lang="ts">
/**
 * Host for the three programme zones: clock, theme tokens and diagnostics.
 *
 * Kept separate from the components so each of them stays a pure function of
 * (params, payload, now) and can be rendered at any instant in a test, while the
 * single awkward piece of statefulness — a ticking clock — lives in one place.
 *
 * Theme tokens arrive as `{ "accent": "#c8102e" }` and are emitted as
 * `--lr42-accent`, the same mechanism the text zone already uses. The API
 * validates the token names and values on write (`ZoneThemeSchema`), so no
 * escaping happens here.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { ProgrammePayload, ProgrammeZoneParams } from './programme-types';
import { isUnboundScreenZone, type ProgrammeStrings } from './programme';
import PerformanceDetail from './PerformanceDetail.vue';
import PerformanceSchedule from './PerformanceSchedule.vue';
import SponsorStrip from './SponsorStrip.vue';

const props = defineProps<{
  params: ProgrammeZoneParams;
  payload: ProgrammePayload;
  /** Fixed instant to render at. Omit for a live clock. */
  now?: Date | undefined;
  strings?: ProgrammeStrings | undefined;
  /** Zone name, so a diagnostic warning names the zone an operator has to fix. */
  zoneName?: string | undefined;
}>();

const emit = defineEmits<{
  warn: [message: string];
}>();

// A sponsor strip turns its page on the rotation interval, whose floor is 3
// seconds, so it needs a faster tick than a board whose content changes at
// curtain time.
const TICK_MS = 15_000;
const SPONSOR_TICK_MS = 1_000;

const tick = ref(new Date());
let timer: ReturnType<typeof setInterval> | null = null;

const clock = computed(() => props.now ?? tick.value);

onMounted(() => {
  if (props.now) return;
  const interval = props.params.kind === 'sponsor-strip' ? SPONSOR_TICK_MS : TICK_MS;
  timer = setInterval(() => {
    tick.value = new Date();
  }, interval);
});

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
  timer = null;
});

const themeStyle = computed<Record<string, string>>(() => {
  const style: Record<string, string> = {};
  for (const [token, value] of Object.entries(props.params.theme ?? {})) {
    style[`--lr42-${token}`] = value;
  }
  return style;
});

/**
 * A `screen`-scoped zone on a screen with no location binding renders empty and
 * says so in the heartbeat. The alternative — falling back to every location —
 * would make a mis-provisioned screen look plausibly correct for a whole season
 * (D4).
 */
const unbound = computed(() => isUnboundScreenZone(props.payload, props.params));

watch(
  unbound,
  (isUnbound) => {
    if (isUnbound) emit('warn', `programme-zone-unbound:${props.zoneName ?? props.params.kind}`);
  },
  { immediate: true },
);

// Warnings the server attached to the payload (unknown location ids, an
// organization with no locations yet) travel the same route.
watch(
  () => props.payload.warnings,
  (warnings) => {
    for (const warning of warnings) emit('warn', `programme:${warning.code}`);
  },
  { immediate: true },
);
</script>

<template>
  <div class="lr42-programme" :style="themeStyle">
    <PerformanceDetail
      v-if="params.kind === 'performance-detail'"
      :params="params"
      :payload="payload"
      :now="clock"
      :strings="strings"
    />
    <PerformanceSchedule
      v-else-if="params.kind === 'performance-schedule'"
      :params="params"
      :payload="payload"
      :now="clock"
      :strings="strings"
    />
    <SponsorStrip v-else :params="params" :payload="payload" :now="clock" />
  </div>
</template>

<style scoped>
.lr42-programme {
  width: 100%;
  height: 100%;
  overflow: hidden;
}
</style>
